import React, { useEffect, useState } from "react";
import { ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import KeyboardDoubleArrowUpOutlinedIcon from "@mui/icons-material/KeyboardDoubleArrowUpOutlined";
import { Box, Typography } from "@mui/material";
import { getColor } from "../../style/color/color";
import { String } from "../../constants/Strings";
import { fonts } from "../../style/fonts/fonts";

//indication boxes
const LegendItem = ({ name, value, color }) => (
  <Box
    display="flex"
    alignItems="center"
    mb={1}
    sx={{
      "@media (max-width: 500px)": {
        flexDirection: "column",
        textAlign: "center",
      },
    }}
  >
    <Box
      sx={{
        width: 60,
        height: 50,
        background: color,
        borderRadius: 2,
        textAlign: "center",
        p: "5px",
        mr: "8px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        color: getColor.PrimaryWhite,
        fontSize: "13px",
        fontWeight: "700",
        flexShrink: 0,
        "@media (max-width: 500px)": {
          width: 50,
          height: 50,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {`${value}%`}
    </Box>
    <Typography
      variant="body2"
      sx={{
        fontWeight: "bold",
        "@media (max-width: 500px)": {
          fontSize: "10px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          mt: "5px",
          mr: "10px",

          textAlign: "center",
        },
      }}
    >{`${name}`}</Typography>
  </Box>
);

//color get based on type
const getColorByType = (type) => {
  // eslint-disable-next-line default-case
  switch (type) {
    case String.Dashboard.mainDashboard.Stock:
      return getColor.ColorpaletteSuccess300;
    case String.Dashboard.mainDashboard.Bank:
      return getColor.ColorpaletteInformation400;
    case String.Dashboard.mainDashboard.MtutalFund:
      return getColor.ColorpaletteWarning600;
    case String.Dashboard.mainDashboard.Deposit:
      return getColor.ColorpaletteExtendedBurgundy200;
  }
};

const DashboardPieChart = ({ assets }) => {
  const data = assets.map((asset) => ({
    name: asset.type,
    value: asset.percentageShare,
    color: getColorByType(asset.type),
    totalAmount: asset.amount,
    assetRoi: asset.assetRoi,
  }));

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [totalAmountAssetROI, setTotalAMountAssetROI] = useState({
    amount: "",
    assetROI: "",
  });
  const [hasRun, setHasRun] = useState(false);

  useEffect(() => {
    if (!hasRun && assets.length > 0) {
      const data = assets.map((asset) => ({
        name: asset.type,
        value: asset.percentageShare,
        color: getColorByType(asset.type),
        totalAmount: asset.amount,
        assetRoi: asset.assetRoi,
      }));
      setTotalAMountAssetROI({
        amount: data[0].totalAmount,
        assetROI: data[0].assetRoi,
      });
      setHasRun(true);
    }
  }, [assets, hasRun]);

  const handlePieClick = (data, index) => {
    setSelectedIndex(index);
  };
  const handleClick = (data, index) => {
    setTotalAMountAssetROI({
      amount: data.totalAmount,
      assetROI: data.assetRoi,
    });
  };
  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        margin: "0 auto",
        px: 2,
        "@media (max-width: 500px)": {
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "200px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {/* Responsive container for the pie chart */}
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              paddingAngle={5}
              labelLine={false}
              onClick={handlePieClick}
              activeIndex={selectedIndex}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={selectedIndex === index ? entry.color : entry.color}
                  opacity={selectedIndex === index ? 1.5 : 0.5}
                  onClick={() => handleClick(entry, index)}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {/* Centered overlay text */}
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            color: getColor.PrimaryWhite,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: "15px", sm: "13px" },
              fontWeight: "bold",
              mt: "8px",
            }}
          >
            {`₹ ${totalAmountAssetROI.amount}`}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                color: getColor.ExtendedpalettesIrishGreenIG200,
                fontSize: { xs: "13px", sm: "10px" },
                fontFamily: fonts.manrope,
                fontWeight: "700",
                marginTop: "7px",
                marginRight: "4px",
                lineHeight: 1,
              }}
            >
              {`${totalAmountAssetROI.assetROI}%`}
            </Typography>
            <KeyboardDoubleArrowUpOutlinedIcon
              sx={{
                color: getColor.ExtendedpalettesIrishGreenIG200,
                fontSize: { xs: "18px", sm: "18px" },
                fontWeight: "600",
                marginTop: "6px",
                lineHeight: 1,
              }}
            />{" "}
            {/* Responsive icon size */}
          </Box>
          <Typography
            variant="caption"
            sx={{
              fontSize: { xs: "11px", sm: "10px" },
              fontWeight: "500",
              color: getColor.ExtendedpalettesIrishGreenIG200,
              mb: "15px",
            }}
          >
            {String.Dashboard.mainDashboard.perannum}
          </Typography>
        </Box>
      </Box>
      <Box
        spacing={1}
        sx={{
          ml: 3,
          "@media (max-width: 500px)": {
            maxWidthL: "100%",
            display: "flex",
            flexDirection: "row",
          },
        }}
      >
        {data.map((entry, index) => (
          <LegendItem
            key={`legend-${index}`}
            name={entry.name}
            value={`${entry.value.toFixed(2)}`}
            color={entry.color}
            amount={`₹ ${assets[index].amount.toLocaleString()}`}
            roi={`${assets[index].assetRoi}%`}
          />
        ))}
      </Box>
    </Box>
  );
};

export default DashboardPieChart;
